// A curated table mapping analytics event identities to a short, human description of what that event
// means. The admin event displays group raw analytics events into activity *buckets* ("kinds"); a
// bucket's display label is its identity — the `data-analytics-event` value for a custom event
// (e.g. "bug-add"), or the named page-route group for a pageview (e.g. "Project bugs"). We key the
// table by that same identity so a description can be looked up straight from the bucket label.
//
// This is a developer-maintained reference, not generated data: when you add a new
// `data-analytics-event` worth explaining, add a line here. Unknown identities simply have no
// description (the admin UI omits the line), so the table never needs to be exhaustive.
export const EVENT_DESCRIPTIONS: Record<string, string> = {
  // ─── Page-route groups (pageviews) ──────────────────────────────────────────
  'Home': 'Visited the landing/home page.',
  'New project': 'Opened the new-project page to start QA on an app.',
  'Projects list': 'Viewed the list of their projects.',
  'Project': 'Viewed a project overview.',
  'Project bugs': 'Viewed a project’s bugs list.',
  'Project settings': 'Viewed a project’s settings.',
  'Project journeys': 'Visited the retired project journeys route.',
  'Project test-runs': 'Viewed a project’s test runs.',
  'Project pages': 'Viewed a project’s captured pages.',
  'Bug detail': 'Opened a single bug’s detail page.',
  'Task detail': 'Opened a single task’s detail page.',
  'Journey detail': 'Opened a single journey’s detail page.',
  'Processing history': 'Viewed a project’s container processing history.',
  'Guidance': 'Viewed the guidance entries page.',
  'Guidance update': 'Viewed a single guidance update.',
  'Insights': 'Viewed the insights dashboard.',
  'Embedded project': 'Viewed a project through the public embed.',
  'Embedded journey': 'Viewed a journey through the public embed.',
  'Admin': 'Opened the admin dashboard.',
  'Admin · Activity': 'Viewed the admin activity page.',
  'Admin · User detail': 'Viewed a user’s admin detail page.',
  'Admin · Error detail': 'Viewed an MCP error’s admin detail page.',
  'Admin · Session recording': 'Viewed a help-widget session recording.',
  'Admin · Customers': 'Viewed the admin customers page.',
  'Other pages': 'Pageviews on routes without a named group.',

  // ─── Navigation ─────────────────────────────────────────────────────────────
  'nav-sign-in': 'Clicked sign in from the nav.',
  'nav-sign-out': 'Signed out from the nav.',
  'nav-new-project-link': 'Clicked “new project” in the nav.',
  'nav-projects-link': 'Navigated to projects from the nav.',
  'nav-insights-link': 'Navigated to insights from the nav.',
  'nav-help-button': 'Opened the help widget.',
  'nav-discord-link': 'Clicked the Discord link.',
  'nav-user-menu-toggle': 'Opened the user menu.',
  'nav-user-menu-admin': 'Opened admin from the user menu.',
  'nav-user-menu-settings': 'Opened settings from the user menu.',
  'nav-user-menu-guidance': 'Opened guidance from the user menu.',
  'nav-sidebar-collapse': 'Collapsed the sidebar.',
  'nav-sidebar-expand': 'Expanded the sidebar.',
  'nav-sidebar-project-link': 'Opened a project from the sidebar.',

  // ─── Start QA / dashboard ───────────────────────────────────────────────────
  'dashboard-start-qa-submit': 'Submitted the form to start a QA run on a project.',
  'dashboard-start-qa-url-input': 'Edited the target URL when starting QA.',
  'dashboard-start-qa-instructions-input': 'Edited the QA instructions field.',
  'dashboard-start-qa-advanced-toggle': 'Toggled the advanced options when starting QA.',
  'dashboard-start-qa-scope-all': 'Chose to QA the whole app.',
  'dashboard-start-qa-scope-focus': 'Chose to focus QA on a specific area.',
  'dashboard-start-qa-polish-pass-toggle': 'Toggled the polish pass for the QA run.',
  'dashboard-start-qa-login-add': 'Added login credentials for the QA run.',
  'qa-config-budget-input': 'Edited a new project’s custom credit budget.',
  'qa-config-budget-preset': 'Picked a preset credit budget for a new project.',
  'qa-config-budget-custom': 'Switched to a custom credit budget for a new project.',
  'dashboard-agent-cta-open': 'Opened the coding-agent call-to-action.',
  'dashboard-agent-prompt-copy': 'Copied the coding-agent prompt.',
  'settings-api-agent-prompt-copy': 'Copied the coding-agent prompt from Settings → API.',
  'settings-mcp-agent-prompt-copy': 'Copied the coding-agent prompt from Settings → MCP Server.',
  'project-overview-agent-prompt-copy': 'Copied the coding-agent prompt from the project overview.',

  // ─── Bugs ───────────────────────────────────────────────────────────────────
  'bug-add': 'Opened the add-bug form.',
  'bug-add-submit': 'Submitted a new bug.',
  'bug-row-open': 'Expanded a bug row in the list.',
  'bug-status-toggle': 'Changed a bug’s status (open/resolved/etc.).',
  'bug-severity-toggle': 'Changed a bug’s severity.',
  'bug-delete-button': 'Deleted a bug.',
  'bug-notes-save': 'Saved notes on a bug.',
  'bug-report-copy': 'Copied a bug report.',
  'bug-webhook-send-button': 'Sent a bug to the configured webhook.',
  'bug-hide-resolved-toggle': 'Toggled hiding resolved bugs.',
  'bug-journey-link': 'Followed the link from a bug to its journey.',
  'bug-source-link': 'Followed a bug’s source link.',

  // ─── Journeys ───────────────────────────────────────────────────────────────
  'journey-run-button': 'Ran a journey.',
  'journey-edit-button': 'Opened the journey editor.',
  'journey-edit-save-button': 'Saved edits to a journey.',
  'journey-delete-confirm-button': 'Confirmed deleting a journey.',
  'journey-child-link': 'Opened a child journey.',
  'journey-run-link': 'Opened a journey run.',

  // ─── Projects ───────────────────────────────────────────────────────────────
  'projects-card-open': 'Opened a project from a project card.',
  'projects-search-input': 'Searched the projects list.',
  'project-header-share-button': 'Opened the project share dialog.',
  'project-header-pause-toggle': 'Paused or resumed a project.',
  'project-header-delete-confirm': 'Confirmed deleting a project.',
  'project-header-name-edit': 'Edited a project’s name.',
  'project-members-invite-submit': 'Invited a member to a project.',
  'project-members-remove-confirm': 'Removed a member from a project.',
  'project-processing-spawn-button': 'Manually spawned a processing container.',

  // ─── Project settings ───────────────────────────────────────────────────────
  'project-settings-webhook-save': 'Saved a project’s webhook settings.',
  'project-settings-github-save': 'Saved a project’s GitHub settings.',
  'project-settings-design-document-save': 'Saved a project’s design document.',
  'project-settings-entrypoint-save': 'Saved a project’s entrypoint URL.',
  'project-settings-public-access-toggle': 'Toggled a project’s public access.',
  'project-settings-active-status-toggle': 'Toggled a project active/inactive.',
  'project-settings-budget-increase-button': 'Raised a project’s credit budget.',
  'project-settings-budget-decrease-button': 'Lowered a project’s credit budget.',
  'project-settings-danger-zone-delete-confirm': 'Confirmed project deletion in the danger zone.',

  // ─── Project overview ─────────────────────────────────────────────────────────
  'project-overview-paused-billing-link': 'Opened billing from the out-of-credits paused banner.',

  // ─── Test runs ──────────────────────────────────────────────────────────────
  'test-run-start-submit': 'Started a new test run.',
  'test-run-row-open': 'Expanded a test run row.',
  'test-run-replay-link': 'Opened a test run’s replay.',
  'test-run-retrigger': 'Re-triggered a test run.',
  'test-run-polish-submit': 'Submitted a polish pass for a test run.',
  'polish-rrweb-data-link': 'Opened the rrweb event data a polish run is using.',
  'polish-injected-data-link': 'Opened the injected-script event data a polish run is using.',

  // ─── Guidance (historical) ──────────────────────────────────────────────────
  // No longer emitted: guidance moved to repo source, so the entry/update/feedback UI is gone. Kept
  // so already-recorded analytics_events rows still describe themselves in the activity feed.
  'guidance-new-entry-button': 'Started creating a new guidance entry.',
  'guidance-create-submit': 'Created a guidance entry.',
  'guidance-entry-save': 'Saved a guidance entry.',
  'guidance-update-submit': 'Submitted a guidance update.',
  'guidance-feedback-approve-button': 'Approved guidance feedback.',
  'guidance-feedback-ignore-button': 'Ignored guidance feedback.',

  // ─── Plans / billing ────────────────────────────────────────────────────────
  'plan-gate-shown': 'Was shown the out-of-credits upgrade paywall.',
  'plan-gate-close-button': 'Dismissed the out-of-credits paywall.',
  'plan-gate-logout-button': 'Logged out from the out-of-credits paywall.',
  'plan-select-upgrade': 'Selected a plan to upgrade to.',
  'plan-contact-sales-link': 'Clicked contact sales on the plans page.',
  'plan-interval-month-toggle': 'Switched plan pricing to monthly.',
  'plan-interval-year-toggle': 'Switched plan pricing to yearly.',
  'settings-manage-subscription-button': 'Opened the subscription management portal.',

  // ─── Settings & tokens ──────────────────────────────────────────────────────
  'settings-token-generate-button': 'Generated an API token.',
  'settings-token-revoke-button': 'Revoked an API token.',
  'settings-token-copy': 'Copied an API token.',
  'settings-notify-on-completion-toggle': 'Toggled completion notifications.',
  'settings-theme-open': 'Opened the theme picker in settings.',
  'settings-theme-set': 'Changed the app theme.',
  'theme-toggle': 'Toggled light and dark mode.',
  'settings-mcp-config-copy': 'Copied the MCP config.',

  // ─── Sharing ────────────────────────────────────────────────────────────────
  'share-make-public-button': 'Made a shared resource public.',
  'share-copy': 'Copied a share link.',

  // ─── Insights ───────────────────────────────────────────────────────────────
  'insights-scorecard-project-link': 'Opened a project from an insights scorecard.',
  'insights-usage-project-toggle': 'Expanded a project’s usage in insights.',

  // ─── Feedback / help ────────────────────────────────────────────────────────
  'feedback-open-button': 'Opened the feedback form.',
}

// Short description of what an event means, looked up by an activity bucket's display label (its
// identity — a `data-analytics-event` value for custom events, or a page-route group for pageviews).
// Returns null for buckets we have no description for, so callers can simply skip rendering the line.
export function eventDescription(label: string): string | null {
  return EVENT_DESCRIPTIONS[label] ?? null
}
